/* eslint-disable security/detect-non-literal-fs-filename */ function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function ownKeys(object, enumerableOnly) {
    var keys = Object.keys(object);
    if (Object.getOwnPropertySymbols) {
        var symbols = Object.getOwnPropertySymbols(object);
        if (enumerableOnly) {
            symbols = symbols.filter(function(sym) {
                return Object.getOwnPropertyDescriptor(object, sym).enumerable;
            });
        }
        keys.push.apply(keys, symbols);
    }
    return keys;
}
function _object_spread_props(target, source) {
    source = source != null ? source : {};
    if (Object.getOwnPropertyDescriptors) {
        Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
        ownKeys(Object(source)).forEach(function(key) {
            Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
        });
    }
    return target;
}
import { jsx as _jsx } from "react/jsx-runtime";
import { createBrowserHistory } from 'history';
import React from 'react';
import { matchRoutes, useLocation, useNavigate, useParams } from 'react-router';
import { Constants } from '@shared/utils/constants';
import { getWindow, getDocument } from '@shared/services/window';
import { IFeatureFlags } from '../State';
import { campaignParams, getAppViewParams } from '@shared/utils/routeUtils';
import { saveDataToBrowserAfterUserAgreedPrivacyConsent } from '@shared/utils/browserStorageUtils';
import { getCanonicalLink } from '@shared/utils/seoUtils';
import { getHostFromBrowserUrl, getProtocolFromBrowserUrl } from './utils/urlUtils';
import { getAppConfig } from '@shared/services/init/appConfig';
import { useTelemetry } from '@shared/hooks/useTelemetry';
import { logger } from '@src/logger';
import { getXssDiffString } from '@shared/utils/xssUtils';
import { scrubField } from '@shared/utils/scrubbingUtils';
const EMBED_PREFIX = '/embed';
let isEmbedded = false;
let appRoutes = [];
export let previousPage = '';
export let appHistory = null;
export const canonicalLink = (originalUrl)=>{
    var _document_documentElement;
    const document = getDocument();
    const canonical = document === null || document === void 0 ? void 0 : (_document_documentElement = document.documentElement) === null || _document_documentElement === void 0 ? void 0 : _document_documentElement.querySelector("link[rel='canonical']");
    if (canonical) {
        const appName = getAppConfig('appName');
        const isMac = appName === Constants.canonicalAMPEnvAppName;
        const host = getHostFromBrowserUrl();
        const protocol = getProtocolFromBrowserUrl();
        const canonicalLinkPrefix = `${protocol}//${host}`;
        const canonicalLink = getCanonicalLink(originalUrl, isMac);
        const documentCanonicalLink = `${canonicalLinkPrefix}${canonicalLink}`;
        canonical.setAttribute('href', documentCanonicalLink);
    }
};
function embedURL(url) {
    return url.startsWith(EMBED_PREFIX) ? url : `${EMBED_PREFIX}${url}`;
}
function isRouteChangingAction(action) {
    return [
        'PUSH',
        'POP',
        'REPLACE'
    ].indexOf(action) > -1;
}
export function isValidRoute(location) {
    const base = isEmbedded ? EMBED_PREFIX : '/';
    return Boolean(matchRoutes(appRoutes, location, base));
}
function onRouteChanged(location) {
    const originalUrl = location.pathname + location.search;
    const payload = {
        page: originalUrl,
        action: Constants.Telemetry.Action.RouteChanged,
        actionModifier: Constants.Telemetry.ActionModifier.Start,
        details: JSON.stringify({
            previousPage
        })
    };
    logger.info(payload.details, {
        action: payload.action,
        actionModifier: payload.actionModifier,
        page: payload.page
    });
    canonicalLink(originalUrl);
}
function refererByCallbackUrl(location) {
    const { pathname, search } = location;
    if (pathname.includes('/callback')) {
        const searchParams = new URLSearchParams(search);
        const state = searchParams.get('state');
        if (!state) {
            return null;
        }
        try {
            const _state = JSON.parse(state);
            const decodedFrom = decodeURIComponent(_state.from);
            const stateHostname = decodeURIComponent(_state.hostName);
            return (decodedFrom === null || decodedFrom === void 0 ? void 0 : decodedFrom.startsWith(stateHostname)) && !getXssDiffString({
                url: decodedFrom
            }) ? decodedFrom : pathname;
        } catch (e) {
            return null;
        }
    }
    return null;
}
export function initRouterHistory(args) {
    var _args_embedded;
    const embedded = (_args_embedded = args.embedded) !== null && _args_embedded !== void 0 ? _args_embedded : false;
    appRoutes = args.appRoutes;
    isEmbedded = embedded;
    appHistory = createBrowserHistory({
        window: getWindow()
    });
    let previousLocation = getWindow().location;
    // in case client receives a /callback path as a result of post-sso-login we want the client to render the referer url and not the callback url
    if (previousLocation.pathname.includes('/callback')) {
        const refererPathname = refererByCallbackUrl(previousLocation);
        if (refererPathname) {
            appHistory.push(refererPathname);
        }
    }
    appHistory.listen((update)=>{
        if (isRouteChangingAction(update.action)) {
            onRouteChanged(previousLocation);
        }
        previousLocation = getWindow().location;
    });
}
// These object defines valid queryParams for the different states in the app.  This allows us to
// 'smartly' drop and add query parameters when navigating between pages
const FilterQuery = Constants.FilterQuery;
export const routeParams = {
    appView: getAppViewParams().concat(Object.keys(new IFeatureFlags())),
    home: [
        'successStoryTab'
    ],
    marketplace: [
        FilterQuery.industry,
        FilterQuery.category,
        FilterQuery.subcategories,
        FilterQuery.subindustries,
        FilterQuery.product,
        'search',
        'showPrivateApps',
        'page',
        'pageSize',
        'serviceType',
        'country',
        'region',
        'certification',
        'filters',
        FilterQuery.sortBy
    ],
    partnersDir: [
        'filter',
        'contact'
    ],
    partnerSuggestion: [
        'text',
        'type',
        'value'
    ],
    blog: [],
    appDetails: [
        'breadcrumbUrl',
        'tab',
        'survey'
    ],
    checkout: [],
    orderConfirmation: [],
    myReviews: [],
    localePicker: [],
    partners: [],
    partnersForm: [],
    partnerDetail: [
        'breadcrumbUrl',
        'tab'
    ],
    sell: [],
    about: [],
    serviceDetail: [
        'breadcrumbUrl'
    ],
    userFavouriteDetail: [],
    privateOffersDetail: [],
    sitemap: [],
    commonPageDetails: [
        'breadcrumbUrl',
        'tab',
        'survey'
    ],
    privateOffersGallery: [
        'page'
    ]
};
// Should update the relevant urls here.
export const routes = {
    localePicker: {
        name: Constants.Views.locale,
        getPath: ()=>'/localepicker',
        params: routeParams.localePicker.concat(routeParams.appView)
    },
    home: {
        name: Constants.Views.home,
        getPath: (_, enableUrlChanges)=>enableUrlChanges ? '/' : '/home',
        params: routeParams.home.concat(routeParams.appView)
    },
    marketplace: {
        name: Constants.Views.appGallery,
        getPath: (_, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newAppsGallery}` : `/${Constants.appsGallery}`,
        params: routeParams.marketplace.concat(routeParams.appView),
        initialParamsValue: {
            page: '1',
            search: null,
            sortBy: null,
            [FilterQuery.filters]: null,
            [FilterQuery.category]: null,
            [FilterQuery.industry]: null,
            [FilterQuery.subcategories]: null,
            [FilterQuery.subindustries]: null,
            [FilterQuery.product]: null,
            serviceType: null
        }
    },
    marketplaceCloudsIndustry: {
        name: Constants.Views.cloudsIndustryGallery,
        getPath: (_, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newCloudsIndustryGallery}` : `/${Constants.cloudsIndustryGallery}`,
        params: routeParams.marketplace.concat(routeParams.appView),
        initialParamsValue: {
            page: '1',
            search: null,
            sortBy: null,
            [FilterQuery.filters]: null,
            [FilterQuery.category]: null,
            [FilterQuery.industry]: null,
            [FilterQuery.subcategories]: null,
            [FilterQuery.subindustries]: null,
            [FilterQuery.product]: null,
            serviceType: null
        }
    },
    appDetails: {
        name: Constants.Views.appDetails,
        getPath: (routeArgs, enableUrlChanges)=>enableUrlChanges ? '/product/' + routeArgs.entityId : '/product/' + routeArgs.productId + '/' + routeArgs.entityId,
        params: routeParams.appDetails.concat(routeParams.appView)
    },
    checkout: {
        name: Constants.Views.checkout,
        getPath: (routeArgs, enableUrlChanges)=>enableUrlChanges ? '/checkout/' + routeArgs.entityId : '/marketplace/checkout/' + routeArgs.entityId,
        params: routeParams.checkout.concat(routeParams.appView)
    },
    orderConfirmation: {
        name: Constants.Views.orderConfirmation,
        getPath: ()=>'/marketplace/order-confirmation',
        params: routeParams.orderConfirmation.concat(routeParams.appView)
    },
    blog: {
        name: Constants.Views.blog,
        getPath: ()=>'/blog',
        params: routeParams.blog.concat(routeParams.appView)
    },
    myReviews: {
        name: Constants.Views.reviews,
        getPath: ()=>'/user/my-reviews',
        params: routeParams.myReviews.concat(routeParams.appView)
    },
    marketing: {
        name: Constants.Views.sell,
        getPath: ()=>'/sell',
        params: routeParams.sell.concat(routeParams.appView)
    },
    about: {
        name: Constants.Views.about,
        getPath: ()=>'/about',
        params: routeParams.about.concat(routeParams.appView)
    },
    billingRegion: {
        name: Constants.Views.billingregion,
        getPath: ()=>'/billingregion',
        params: routeParams.about.concat(routeParams.appView)
    },
    fieldHub: {
        name: Constants.Views.fieldHub,
        getPath: ()=>'/account-hub',
        params: routeParams.about.concat(routeParams.appView)
    },
    marketplaceServices: {
        name: Constants.Views.serviceGallery,
        getPath: (_, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newCsGallery}` : `/${Constants.csGallery}`,
        params: routeParams.marketplace.concat(routeParams.appView),
        featureFlag: 'ServiceOffers'
    },
    serviceDetail: {
        name: Constants.Views.serviceDetails,
        getPath: (routeArgs, enableUrlChanges)=>enableUrlChanges ? '/product/' + routeArgs.entityId : `/${Constants.csGallery}/` + routeArgs.entityId,
        params: routeParams.serviceDetail.concat(routeParams.appView)
    },
    marketplacePartners: {
        name: Constants.Views.partnersHost,
        getPath: (_, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newPartnersAppSource}` : `/${Constants.partnersAppSource}`,
        params: routeParams.partnersDir.concat(routeParams.appView)
    },
    marketplacePartnerDetail: {
        name: Constants.Views.partnerDetails,
        getPath: (routeArgs, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newPartnersAppSource}/` + routeArgs.entityId + '/' + routeArgs.tabId : `/${Constants.partnersAppSource}/` + routeArgs.entityId + '/' + routeArgs.tabId,
        params: routeParams.partnerDetail.concat(routeParams.appView)
    },
    marketplacePartnerDetailContact: {
        name: Constants.Views.partnerDetailsContact,
        getPath: (routeArgs, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newPartnersAppSource}/` + routeArgs.entityId + '/' + routeArgs.tabId + '/contact' : `/${Constants.partnersAppSource}/` + routeArgs.entityId + '/' + routeArgs.tabId + '/contact',
        params: routeParams.partnerDetail.concat(routeParams.appView)
    },
    marketplacePartnerSuggestion: {
        name: Constants.Views.partnerSuggestion,
        getPath: (_, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newPartnersAppSource}/suggestion` : `/${Constants.partnersAppSource}/suggestion`,
        params: routeParams.partnerSuggestion.concat(routeParams.appView)
    },
    userFavouriteDetail: {
        name: Constants.Views.userFavouriteDetail,
        getPath: ()=>'/user/my-favourites',
        params: routeParams.userFavouriteDetail.concat(routeParams.appView)
    },
    privateOffers: {
        name: Constants.Views.privateOffers,
        getPath: ()=>'/user/private-offers',
        params: routeParams.privateOffersGallery.concat(routeParams.appView),
        initialParamsValue: {
            page: '1'
        }
    },
    privateOfferDetails: {
        name: Constants.Views.privateOfferDetails,
        getPath: (routeArgs, enableUrlChanges)=>enableUrlChanges ? `/${Constants.newPrivateOffers}/product/${routeArgs.entityId}` : `/${Constants.privateOffers}/product/${routeArgs.productId}/${routeArgs.entityId}`,
        params: routeParams.appDetails.concat(routeParams.appView)
    },
    campaigns: {
        name: Constants.Views.campaigns,
        getPath: (routeArgs)=>`campaigns/${routeArgs.publisher}/${routeArgs.id}`,
        params: routeParams.sitemap.concat(routeParams.appView)
    },
    sitemap: {
        name: Constants.Views.sitemap,
        getPath: ()=>'/sitemap',
        params: routeParams.sitemap.concat(routeParams.appView)
    },
    redirect: {
        name: Constants.Views.redirect,
        getPath: ()=>'/redirect',
        params: routeParams.sitemap.concat(routeParams.appView)
    },
    commonPageDetails: {
        name: Constants.Views.commonPageDetails,
        getPath: (routeArgs)=>'/product/' + routeArgs.entityId,
        params: routeParams.appDetails.concat(routeParams.appView)
    }
};
// TODO: Support L2 categories
export function buildHref(route, oldRouteParams, newRouteParams, oldParams, newParams, keepAllParams, locale, keepCampaignParams) {
    var _getWindow___INITIAL_STATE__, _getWindow;
    const validQueryParams = route.params;
    // merge old route params
    if (oldRouteParams) {
        const routeKeys = Object.keys(oldRouteParams);
        if (!newRouteParams) {
            newRouteParams = {};
        }
        routeKeys.forEach(function(key) {
            if (oldRouteParams && newRouteParams[`${key}`] === undefined) {
                newRouteParams[`${key}`] = oldRouteParams[`${key}`];
            }
        });
    }
    const enableUrlChanges = !((_getWindow = getWindow()) === null || _getWindow === void 0 ? void 0 : (_getWindow___INITIAL_STATE__ = _getWindow.__INITIAL_STATE__) === null || _getWindow___INITIAL_STATE__ === void 0 ? void 0 : _getWindow___INITIAL_STATE__.config.isEmbedded);
    let path = route.getPath(newRouteParams, enableUrlChanges);
    if (locale) {
        path = '/' + locale + path;
    }
    const targetParams = {};
    for(const op in oldParams){
        targetParams[`${op}`] = oldParams[`${op}`];
    }
    if (!keepCampaignParams) {
        campaignParams.forEach((campaignParam)=>{
            if (targetParams[`${campaignParam}`]) {
                delete targetParams[`${campaignParam}`];
            }
        });
    }
    for(const np in newParams){
        const v = newParams[`${np}`];
        if (!v) {
            if (targetParams[`${np}`]) {
                delete targetParams[`${np}`];
            }
            continue;
        }
        const startChar = v.charAt(0);
        // ';' leading a string means to append to exist params
        if (startChar === ';') {
            const newParam = v.substr(1);
            // No existing params for the current key
            if (!oldParams[`${np}`]) {
                targetParams[`${np}`] = newParam;
            } else {
                const splitParams = newParam.split(';');
                targetParams[`${np}`] = splitParams.reduce((acc, key)=>{
                    const keyLength = key.length;
                    const keyIndex = targetParams[`${np}`].indexOf(key);
                    // make sure it's not a subset of another item
                    // e.g web-apps: starter-web-apps or web-apps-framework
                    // the word web-apps is found in both however it is also an option on its own
                    if (keyIndex > 0) {
                        const previousCharacter = targetParams[`${np}`][keyIndex - 1];
                        const endCharacter = targetParams[`${np}`][keyIndex + keyLength];
                        // if it is encapsulated by ';'
                        // or preceded by ';' and is the last word
                        // it means we found the perfect match
                        if (previousCharacter === ';' && (!endCharacter || endCharacter === ';')) {
                            return acc;
                        }
                    }
                    return acc + ';' + key;
                }, targetParams[`${np}`]);
            // should split and join without dupes
            }
        } else if (startChar === '!') {
            const newParam = v.substr(1);
            if (oldParams[`${np}`]) {
                // should split and join without dupes
                const splitNewParams = newParam.split(';');
                const splitOldParams = oldParams[`${np}`].split(';');
                // eslint-disable-next-line array-callback-return
                splitNewParams.map((key)=>{
                    const pos = splitOldParams.indexOf(key);
                    if (pos >= 0) {
                        splitOldParams.splice(pos, 1);
                    }
                });
                targetParams[`${np}`] = splitOldParams.join(';');
                if (targetParams[`${np}`].length === 0) {
                    delete targetParams[`${np}`];
                }
            }
        } else {
            targetParams[`${np}`] = v;
        }
    }
    // DIY createHref because browserHistory doesn't work
    // return browserHistory.createHref({ pathname: path, query: targetParams });
    let queryString = '';
    let emptyQuery = true;
    for(const tp in targetParams){
        if (validQueryParams.indexOf(tp) >= 0 || validQueryParams.indexOf(tp && tp.toLowerCase()) >= 0 || keepAllParams) {
            queryString += (emptyQuery ? '?' : '&') + tp + '=' + encodeURIComponent(targetParams[`${tp}`]);
            emptyQuery = false;
        }
    }
    const relativeUrl = `${path}${queryString}`;
    return relativeUrl;
}
export function appendQueryParams(path, queryParams) {
    if (!path) {
        path = '';
    }
    if (queryParams) {
        const keys = Object.keys(queryParams);
        if (keys.length > 0) {
            path += path.indexOf('?') > 0 ? '&' : '?';
            path += keys.map((k)=>k + '=' + queryParams[`${k}`]).join('&');
        }
    }
    return path;
}
// This function returns a full relative path
// for '/embed' paths in the embedded app, it will remove the '/embed' part
export const getCurrentRelativeUrl = ()=>{
    const currWindow = getWindow();
    let breadcrumbUrl = currWindow.location.pathname;
    if (currWindow.location.search) {
        breadcrumbUrl += currWindow.location.search;
    }
    if (breadcrumbUrl.indexOf(EMBED_PREFIX) === 0) {
        breadcrumbUrl = breadcrumbUrl.substr(6);
    }
    return breadcrumbUrl;
};
export const urlPush = (location, scrollToTop = false)=>{
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const [{ pageView }] = useTelemetry();
    if (!appHistory) {
        return;
    }
    previousPage = getWindow().location.href;
    const data = {
        previousPage
    };
    const payload = {
        page: location,
        action: Constants.Telemetry.Action.PageLoad,
        actionModifier: Constants.Telemetry.ActionModifier.Start,
        details: JSON.stringify(data)
    };
    logger.info(payload.details, {
        action: payload.action,
        actionModifier: payload.actionModifier,
        page: payload.page
    });
    // The embedded app should never push anything onto the history stack
    if (isEmbedded) {
        const embedLocation = embedURL(location);
        if (isValidRoute(embedLocation)) {
            appHistory.replace(embedLocation); // CodeQL [SM01507] already checking if safe redirect by checking isValidRoute
        }
    } else {
        // save cookie and local storage if needed and not show cookie banner, until the cookie is expired (which is for 13 months)
        saveDataToBrowserAfterUserAgreedPrivacyConsent();
        if (isValidRoute(location)) {
            appHistory.push(location);
        }
        if (scrollToTop) {
            scrollTo(0, 0);
        }
    }
    pageView({
        referrerUri: scrubField(previousPage)
    });
};
export const urlReplace = (location)=>{
    if (!appHistory) {
        return;
    }
    // save cookie and local storage if needed and not show cookie banner, until the cookie is expired (which is for 13 months)
    saveDataToBrowserAfterUserAgreedPrivacyConsent();
    previousPage = getWindow().location.href;
    const nextLocation = isEmbedded ? embedURL(location) : location;
    if (isValidRoute(nextLocation)) {
        appHistory.replace(nextLocation); // CodeQL [SM01507] already checking if safe redirect by checking isValidRoute
    }
};
export const urlBack = (fallbackLocation)=>{
    if (appHistory && previousPage) {
        appHistory.back();
    } else if (fallbackLocation) {
        urlPush(fallbackLocation);
    }
};
/**
 * simple assign to window.location.href
 * @param href should be fully constructed, with host, pathname, and etc.
 */ export const urlAssign = (href, shouldOpenInNewTab)=>{
    if (shouldOpenInNewTab) {
        getWindow() && getWindow().open(href, '_blank');
    } else if (getWindow() && getWindow().location) {
        getWindow().location.href = href;
    }
};
export function getQueryParamsObject(search) {
    const searchParams = new URLSearchParams(search);
    const result = {};
    searchParams.forEach((value, key)=>{
        result[`${key}`] = value;
    });
    return result;
}
export const removeUserUniqueQueryParams = ()=>{
    const currentLocation = getWindow().location;
    const urlToReplace = new URL(currentLocation.href);
    if (urlToReplace.searchParams.get(Constants.QueryStrings.CorrelationId)) {
        urlToReplace.searchParams.delete(Constants.QueryStrings.CorrelationId);
    }
    if (urlToReplace.searchParams.get(Constants.QueryStrings.Referer)) {
        urlToReplace.searchParams.delete(Constants.QueryStrings.Referer);
    }
    if (urlToReplace.href !== currentLocation.href) urlReplace(urlToReplace.href);
};
/**
 * @deprecated
 * This function exists for migrating react-router v3 to react-router-dom v6
 * and will serve only legacy Class Components that haven't been transitioned to functional components
 * Please use the relevant hooks instead in functional components
 */ export function withRouter(Component) {
    return function ComponentWithRouter(props) {
        const location = useLocation();
        const navigate = useNavigate();
        const params = useParams();
        const query = getQueryParamsObject(location === null || location === void 0 ? void 0 : location.search);
        return /*#__PURE__*/ _jsx(Component, _object_spread_props(_object_spread({}, props), {
            // @ts-expect-error location with backwards compatible query
            location: _object_spread_props(_object_spread({}, location), {
                query
            }),
            params: params,
            router: {
                location,
                navigate,
                params
            },
            routeParams: params
        }));
    };
}
